import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, ChevronUp, ChevronDown, Search, Mic, Loader2, X } from "lucide-react";
import { ApertureEye } from "@/components/ApertureEye";
import { CircularAudioIcon } from "@/components/CircularAudioIcon";
import { AssistantAvatar } from "@/components/AssistantAvatar";
import { TaskTimeClocks } from "@/components/TaskTimeClocks";
import { WeatherCard } from "@/components/WeatherCard";
import { unlockAudio } from "@/lib/tts";
import { api } from "@/api";
import { toast } from "sonner";
import { daysDiff, formatTime, formatDate, taskBodyPreview, isLinkTarget } from "@/utils";
import { SafeLinkText } from "@/components/SafeLinkText";
import { groupTasks, windowBucket, windowCards, countOpenObligationsOn, cardOffset, doubleTapTarget } from "../screensaverModel";

/**
 * Timeline-navigated screensaver.
 * HORIZONTAL axis (swipe L/R): time windows
 *   [Completed] · [Older overdue] · [Overdue] · [Today] · [Tomorrow] · [This week] · [Later] · [Someday]
 *   Swipe RIGHT → past (toward Completed).
 *   Swipe LEFT  → future (toward Someday).
 * VERTICAL axis (swipe U/D): tasks WITHIN the current window (TikTok-style, one task fills the screen).
 *   Swipe UP → next task. Swipe DOWN → previous task.
 * Double-tap or Escape exits.
 */

const WINDOWS = [
    // Completed removed per user request — history lives in the main app, not the screensaver focus.
    { id: "older", label: "Older Overdue", subtitle: "> 30 days ago", tone: "rose", empty: "you're caught up." },
    { id: "overdue", label: "Overdue", subtitle: "last 30 days", tone: "red", empty: "nothing overdue." },
    { id: "today", label: "Today", subtitle: "focus", tone: "fuchsia", empty: "the day is yours." },
    { id: "tomorrow", label: "Tomorrow", subtitle: "next up", tone: "violet", empty: "clear skies for tomorrow." },
    { id: "week", label: "This Week", subtitle: "next 7 days", tone: "cyan", empty: "no events this week." },
    { id: "later", label: "Later", subtitle: "next 30 days", tone: "sky", empty: "the horizon is clear.", dynamicLabel: "monthAhead" },
    { id: "someday", label: "Someday", subtitle: "long horizon", tone: "indigo", empty: "no long-term plans yet." },
];
const START_IDX = WINDOWS.findIndex((w) => w.id === "today");

const toneClass = {
    // `opposite`         = solid inverse text color (fuchsia ↔ cyan)
    // `oppositeGradient` = letter gradient dominated by the OPPOSITE hue so the title never blends into the tinted backdrop
    emerald: { text: "text-emerald-300", opposite: "text-meye-fuchsia", oppositeGradient: "from-meye-fuchsia via-pink-200 to-meye-fuchsia", border: "border-emerald-300",  bg: "bg-emerald-500/10", glow: "rgba(52,211,153,0.5)",  stroke: "stroke-meye-fuchsia", solid: "#6ee7b7", gradient: "from-meye-cyan to-meye-fuchsia" },
    rose:    { text: "text-rose-300",    opposite: "text-meye-cyan",    oppositeGradient: "from-meye-cyan via-cyan-200 to-meye-cyan",       border: "border-rose-300",     bg: "bg-rose-500/10",    glow: "rgba(251,113,133,0.5)", stroke: "stroke-meye-cyan",    solid: "#fda4af", gradient: "from-meye-cyan to-meye-fuchsia" },
    red:     { text: "text-red-400",     opposite: "text-meye-cyan",    oppositeGradient: "from-meye-cyan via-cyan-200 to-meye-cyan",       border: "border-red-300",      bg: "bg-red-500/10",     glow: "rgba(248,113,113,0.55)",stroke: "stroke-meye-cyan",    solid: "#fca5a5", gradient: "from-meye-cyan to-meye-fuchsia" },
    fuchsia: { text: "text-meye-fuchsia",opposite: "text-meye-cyan",    oppositeGradient: "from-meye-cyan via-cyan-200 to-meye-cyan",       border: "border-meye-fuchsia", bg: "bg-meye-fuchsia/10",glow: "rgba(217,70,239,0.55)", stroke: "stroke-meye-cyan",    solid: "#e879f9", gradient: "from-meye-fuchsia to-meye-cyan" },
    violet:  { text: "text-violet-300",  opposite: "text-meye-cyan",    oppositeGradient: "from-meye-cyan via-cyan-200 to-meye-cyan",       border: "border-violet-300",   bg: "bg-violet-500/10",  glow: "rgba(167,139,250,0.5)", stroke: "stroke-meye-cyan",    solid: "#c4b5fd", gradient: "from-meye-fuchsia to-meye-cyan" },
    cyan:    { text: "text-meye-cyan",   opposite: "text-meye-fuchsia", oppositeGradient: "from-meye-fuchsia via-pink-200 to-meye-fuchsia", border: "border-meye-cyan",    bg: "bg-meye-cyan/10",   glow: "rgba(34,211,238,0.55)", stroke: "stroke-meye-fuchsia", solid: "#67e8f9", gradient: "from-meye-cyan to-meye-fuchsia" },
    sky:     { text: "text-sky-300",     opposite: "text-meye-fuchsia", oppositeGradient: "from-meye-fuchsia via-pink-200 to-meye-fuchsia", border: "border-sky-300",      bg: "bg-sky-500/10",     glow: "rgba(125,211,252,0.5)", stroke: "stroke-meye-fuchsia", solid: "#7dd3fc", gradient: "from-meye-cyan to-meye-fuchsia" },
    indigo:  { text: "text-indigo-300",  opposite: "text-meye-fuchsia", oppositeGradient: "from-meye-fuchsia via-pink-200 to-meye-fuchsia", border: "border-indigo-300",   bg: "bg-indigo-500/10",  glow: "rgba(165,180,252,0.5)", stroke: "stroke-meye-fuchsia", solid: "#a5b4fc", gradient: "from-meye-fuchsia to-meye-cyan" },
};

export const Screensaver = ({ tasks, userName, assistantName, assistantAvatarId, contextScan, onExit, onOpenComms, onOpenSection, onSearch, onQuickCaptured }) => {
    const [now, setNow] = useState(new Date());
    // Compute initial window ONCE from the first tasks payload: if there's anything overdue,
    // land on OVERDUE first (user request). Otherwise fall back to TODAY. This runs lazily so
    // late-loading tasks (initial fetch) don't re-open the screensaver on a different pane.
    const initialWIdx = React.useMemo(() => {
        const overdueCount = (tasks || []).filter((t) => {
            if (t.archived || t.completed || t.type === "recurring" || !t.date) return false;
            const d = daysDiff(t.date);
            return d !== null && d < 0 && d >= -30;
        }).length;
        const olderCount = (tasks || []).filter((t) => {
            if (t.archived || t.completed || t.type === "recurring" || !t.date) return false;
            const d = daysDiff(t.date);
            return d !== null && d < -30;
        }).length;
        if (overdueCount) return WINDOWS.findIndex((w) => w.id === "overdue");
        if (olderCount)   return WINDOWS.findIndex((w) => w.id === "older");
        return START_IDX;
    }, []); // run only on mount
    const [wIdx, setWIdx] = useState(initialWIdx);
    const [tIdx, setTIdx] = useState(0);
    // Badge style toggle — remembered locally between visits. Default = gradient.
    const [badgeStyle, setBadgeStyle] = useState(() => {
        try { return localStorage.getItem("meye_badge_style") || "gradient"; } catch { return "gradient"; }
    });
    useEffect(() => {
        try { localStorage.setItem("meye_badge_style", badgeStyle); } catch { /* ignore */ }
    }, [badgeStyle]);
    const lastTapRef = useRef(0);
    const firstTapTaskRef = useRef(null);
    const firstTapHitTaskRef = useRef(false);  // was the first tap's hit-test on the task body?
    const lastDragEndRef = useRef(0);
    const lastInteractionRef = useRef(Date.now());
    // Attached to the on-screen task body so we can hit-test the tap against its bounding rect.
    // Only a tap that lands INSIDE this rect should open the edit dialog on double-tap. Any tap
    // outside (empty gutters / peripheral chrome) is treated as a "just exit to main page" gesture.
    const taskBodyRef = useRef(null);

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    const groups = useMemo(() => groupTasks(tasks, new Date()), [tasks]);
    const currentWindow = WINDOWS[wIdx];
    // "This Week" is a superset that includes Today + Tomorrow + the rest of the week (day 2-7).
    // "Later" spans the next ~30 days including everything already covered by earlier windows.
    // Sort by day-offset (then by time) so vertical flicks always advance to the NEXT chronological
    // task — never jumping backward in the calendar.
    // Window assembly lives in the pure model (screensaverModel.windowCards) — the component only renders.
    const currentTasks = useMemo(() => windowCards(groups, currentWindow.id, new Date()), [groups, currentWindow.id]);
    const currentTask = currentTasks[tIdx];

    // Clamp tIdx if tasks shrink AND reset to 0 on window changes. Without this reset, swiping
    // from Today (say, tIdx=3) to Tomorrow (only 1 task) leaves `currentTask` = undefined and the
    // EmptyCard renders — which is why double-tap "did nothing" on the next window: there was no
    // task to open. Snap back to the first task of the new window every time wIdx changes.
    const prevWIdxRef = useRef(wIdx);
    useEffect(() => {
        if (prevWIdxRef.current !== wIdx) {
            prevWIdxRef.current = wIdx;
            setTIdx(0);
            return;
        }
        if (tIdx >= currentTasks.length) setTIdx(0);
    }, [wIdx, currentTasks.length, tIdx]);

    // Auto-advance: every 10s → next task; PAUSED for 6s after any user interaction
    useEffect(() => {
        const id = setTimeout(() => {
            // Skip if user recently interacted (prevents the task changing under the finger between taps)
            if (Date.now() - lastInteractionRef.current < 6000) return;
            if (currentTasks.length > 0 && tIdx < currentTasks.length - 1) {
                setTIdx((i) => i + 1);
            } else {
                setWIdx((w) => (w + 1) % WINDOWS.length);
                setTIdx(0);
            }
        }, 10000);
        return () => clearTimeout(id);
    }, [wIdx, tIdx, currentTasks.length]);

    const bumpInteraction = () => {
        lastInteractionRef.current = Date.now();
    };

    const nextWindow = () => {
        bumpInteraction();
        setWIdx((w) => (w + 1) % WINDOWS.length);
        setTIdx(0);
    };
    const prevWindow = () => {
        bumpInteraction();
        setWIdx((w) => (w - 1 + WINDOWS.length) % WINDOWS.length);
        setTIdx(0);
    };
    const nextTask = () => {
        if (currentTasks.length === 0) return;
        bumpInteraction();
        setTIdx((i) => (i + 1) % currentTasks.length);
    };
    const prevTask = () => {
        if (currentTasks.length === 0) return;
        bumpInteraction();
        setTIdx((i) => (i - 1 + currentTasks.length) % currentTasks.length);
    };

    // Called from the location chip in the TimelineScrubber. Attempts to jump the card cycle to
    // the surfaced task; falls back to a window sweep if the task lives in a different bucket.
    const handleLocationTap = (taskId) => {
        if (!taskId) return;
        bumpInteraction();
        for (let wi = 0; wi < WINDOWS.length; wi++) {
            const target = WINDOWS[wi];
            const bucket = windowBucket(groups, target.id);
            const idx = bucket.findIndex((t) => t.id === taskId);
            if (idx >= 0) {
                setWIdx(wi);
                setTIdx(idx);
                return;
            }
        }
    };

    // Called from the DayStrip when the user taps a day. Only fires for days with at least
    // one task (see DayStrip.handleCellClick). Jumps the card cycle to the first task on that
    // day. In the "later" (30-day) window a tapped date may live in an earlier bucket that the
    // combined list already includes (today/tomorrow/week/later) — findIndex covers all of them.
    const handleSelectOffset = (offset) => {
        if (!currentTasks.length) return;
        bumpInteraction();
        // In week/later windows currentTasks carry `_occurrenceOffset` so a single daily-recurring
        // task can be scrolled to on any day it occurs. Fall back to dayOffsetOf for other windows.
        const idx = currentTasks.findIndex((t) => cardOffset(t) === offset);
        if (idx >= 0) setTIdx(idx);
    };

    // Double-tap behaviour. Hit-tests the tap point against the task body's bounding rect:
    //   • double-tap INSIDE the task text/pill → open the editor for that occurrence
    //   • double-tap OUTSIDE (empty gutter, dark area) → just exit to the main app / home
    // Both are 550 ms windows (more forgiving than iOS's 400 ms system default).
    //
    // FORGIVING hit-zone (learned the hard way — see git log 9ec6b65 → f7cd52b): the RAW
    // bounding rect of `taskBodyRef` is unreliable — it shifts during entry animation, is
    // narrow on short task titles (~40px tall h2), and off-center thumb placement routinely
    // misses. That produced the "I double-tapped the task and it went home" complaint. We
    // fix by (a) generously padding the rect so a near-miss still counts as ON, and
    // (b) failing SAFE: if the ref isn't measured yet or reports a degenerate rect, treat
    // as ON — the primary action shouldn't punish the user when the DOM isn't ready.
    const isInsideTaskBody = (point) => {
        if (!point) return false;
        const el = taskBodyRef.current;
        if (!el) return true;  // fail safe → primary action wins during entry animation
        const r = el.getBoundingClientRect();
        if (r.width < 20 || r.height < 20) return true;  // degenerate rect → treat as on
        const PAD_X = 30;
        const PAD_Y = 60;
        return (
            point.x >= r.left - PAD_X && point.x <= r.right + PAD_X &&
            point.y >= r.top  - PAD_Y && point.y <= r.bottom + PAD_Y
        );
    };
    const onTapAny = (event, info) => {
        // A tap that started on a rendered link is the link's — never a Screensaver tap.
        if (isLinkTarget(event?.target)) return;
        // Suppress any tap that fires close to a drag — protects against flicks that Framer Motion
        // interprets as taps because the pointer moved less than its internal tap-move threshold.
        if (Date.now() - lastDragEndRef.current < 500) return;
        bumpInteraction();
        const t = Date.now();
        const insideNow = isInsideTaskBody(info?.point);
        if (t - lastTapRef.current < 550) {
            // Second tap of a double-tap. Open the editor only if BOTH taps landed inside the
            // task body; otherwise treat the double-tap as a "just get me out" gesture and exit
            // without a task (main app / home).
            const bothOnTask = firstTapHitTaskRef.current && insideNow;
            const target = bothOnTask ? (firstTapTaskRef.current || currentTask || null) : null;
            firstTapTaskRef.current = null;
            firstTapHitTaskRef.current = false;
            lastTapRef.current = 0;
            // Identity comes from the card itself (_occKey/_displayIso) — never from "next occurrence".
            onExit?.(doubleTapTarget(target));
        } else {
            lastTapRef.current = t;
            firstTapTaskRef.current = currentTask || null;
            firstTapHitTaskRef.current = insideNow;
        }
    };

    // Kill any pending single-tap latch the instant a drag starts. If the user starts a flick
    // right after tapping once, we must NOT interpret the flick's onTap (if it fires) as the
    // second tap of a double-tap.
    //
    // IMPORTANT: we do NOT clear firstTapTaskRef here — a very slight finger jitter on the
    // second tap of a double-tap fires onDragStart before onTap, and clearing eagerly here
    // would lose the target so the editor never opens (the double-tap "went home" instead).
    // We only clear the ref in onDragEnd if the movement actually crossed the swipe threshold.
    const onDragStart = () => { /* intentionally no-op — see onDragEnd */ };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" || e.key === "Enter") onExit?.();
            else if (e.key === "ArrowLeft") nextWindow();  // swipe LEFT = future
            else if (e.key === "ArrowRight") prevWindow(); // swipe RIGHT = past
            else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                // NOTE: swipe UP means finger moves up on screen -> next task
                // ArrowDown key = "next" in traditional navigation, ArrowUp = "prev"
                // We map ArrowDown -> nextTask, ArrowUp -> prevTask (matches page navigation)
                if (e.key === "ArrowDown") nextTask();
                else prevTask();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onExit, currentTasks.length]);

    const onDragEnd = (_, info) => {
        lastDragEndRef.current = Date.now();
        const { x, y } = info.offset;
        const vx = info.velocity?.x || 0;
        const vy = info.velocity?.y || 0;
        const H = Math.abs(x), V = Math.abs(y);
        // A swipe fires if the drag moved past a modest distance OR the flick was fast enough.
        // Thresholds tuned DOWN from 40px/400vel → 22px/220vel because the previous values
        // routinely swallowed short thumb flicks on phones and made the screensaver feel
        // sluggish. 22px is still comfortably above accidental micro-drags.
        const DIST = 22, VEL = 220;
        const horizSwipe = H > V && (H > DIST || Math.abs(vx) > VEL);
        const vertSwipe = V >= H && (V > DIST || Math.abs(vy) > VEL);
        if (!horizSwipe && !vertSwipe) return;   // micro-drag = still a tap candidate; keep firstTapTaskRef intact
        // Real swipe — the pending double-tap is dead. Clear the latch so a subsequent tap
        // doesn't accidentally pair with the pre-swipe tap.
        lastTapRef.current = 0;
        firstTapTaskRef.current = null;
        firstTapHitTaskRef.current = false;
        // 📳 Haptic tick — 15ms crisp buzz that confirms the swipe committed. Horizontal
        // (window change) gets a single tick; vertical (task cycle) gets a double-tap so the
        // two axes feel physically different in your hand. `navigator.vibrate` is a no-op on
        // desktop and on iOS Safari, so this is safely additive.
        try {
            if (horizSwipe) navigator.vibrate?.(15);
            else navigator.vibrate?.([10, 20, 10]);
        } catch (_) { /* not available */ }
        if (horizSwipe) {
            if (x < 0 || vx < -VEL) nextWindow();
            else prevWindow();
        } else {
            if (y < 0 || vy < -VEL) nextTask();
            else prevTask();
        }
    };

    return (
        <div
            role="button"
            tabIndex={0}
            data-testid="screensaver"
            className="letter-scope fixed inset-0 z-[100] overflow-hidden select-none cursor-pointer bg-black"
        >
            <div className="absolute inset-0 bg-[#050505]" />
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                <div className="ss-blob ss-blob-1" />
                <div className="ss-blob ss-blob-2" />
                <div className="ss-blob ss-blob-3" />
                <div className="ss-blob ss-blob-4" />
            </div>
            <div
                className="absolute inset-0 pointer-events-none"
                aria-hidden="true"
                style={{
                    background:
                        "radial-gradient(ellipse at center, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.15) 70%, rgba(0,0,0,0) 100%)",
                }}
            />
            <div className="pointer-events-none absolute inset-0 opacity-30">
                <div className="scan-line" />
            </div>

            {/* Top strip: time-window scrubber */}
            <TimelineScrubber
                wIdx={wIdx}
                now={now}
                userName={userName}
                onOpenComms={onOpenComms}
                onQuickCaptured={onQuickCaptured}
                windowTasks={currentTasks}
                contextScan={contextScan}
                onLocationTap={handleLocationTap}
            />

            {/* Right rail: task pagination (when >1 task) */}
            {currentTasks.length > 1 && (
                <TaskRail tIdx={tIdx} total={currentTasks.length} tone={currentWindow.tone} />
            )}

            {/* Hint — z-30 to sit above the swipe layer */}
            <div className="absolute bottom-24 left-0 right-0 flex flex-col items-center gap-3 pointer-events-none z-30">
                <p
                    className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-mono"
                    data-testid="screensaver-hint"
                >
                    swipe · double-tap to edit the task · swipe on the dock to open a section
                </p>
            </div>

            {/* Section shortcut dock — always above the swipe layer so taps aren't stolen by drag */}
            {onSearch && (
                <ScreensaverSearchDock
                    onSearch={onSearch}
                    onOpenComms={onOpenComms ? () => onOpenComms(currentWindow) : undefined}
                    onQuickCaptured={onQuickCaptured}
                    assistantName={assistantName}
                    assistantAvatarId={assistantAvatarId}
                />
            )}

            {/* Content — swipeable in both axes. Outer motion.div handles drag+tap (never re-keys),
                inner AnimatePresence cross-dissolves the slide content. */}
            <motion.div
                drag
                dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
                dragElastic={0.5}
                dragMomentum={false}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onTap={onTapAny}
                className="absolute inset-0 touch-none"
                data-testid={`screensaver-window-${currentWindow.id}`}
            >
                <AnimatePresence>
                    <motion.div
                        key={`${wIdx}-${tIdx}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.55, ease: "easeInOut" }}
                        className={`absolute inset-0 flex flex-col items-center px-6 text-center pointer-events-none ${
                            currentWindow.id === "later"
                                ? "justify-start pt-32 pb-40"
                                : "justify-center pt-14 pb-36"
                        }`}
                    >
                        {currentTask ? (
                            <TaskFocusCard bodyRef={taskBodyRef} task={currentTask} window={currentWindow} badgeStyle={badgeStyle} windowTasks={currentTasks} onSelectOffset={handleSelectOffset} />
                        ) : (
                            <EmptyCard window={currentWindow} />
                        )}
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

// ---------- Timeline scrubber (top) ----------
const TimelineScrubber = ({ wIdx, now, userName, onOpenComms, onQuickCaptured, windowTasks = [], contextScan, onLocationTap }) => {
    const w = WINDOWS[wIdx];
    const tone = toneClass[w.tone];
    const dateStr = now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const timeStr = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

    // Split the current window's items into "events" (anything with a time OR a recurring cadence)
    // vs plain "tasks". Recurring items are always events since they represent scheduled cadence.
    // Dedupe by id — a recurring task that hits multiple buckets (today + tomorrow + week) is
    // otherwise counted multiple times in `windowTasks`, which inflated the top-of-window pill.
    const uniqWindowTasks = (() => {
        const seen = new Set();
        // Key by obligation, not task — a beat moved onto this day is a separate card/event.
        return windowTasks.filter((t) => { if (!t) return false; const k = `${t.id}:${t._occKey || ""}`; return !seen.has(k) && seen.add(k); });
    })();
    const eventCount = uniqWindowTasks.filter((t) => !!t.time || t.type === "recurring").length;
    const taskCount = uniqWindowTasks.length - eventCount;

    return (
        <div className="absolute top-0 left-0 right-0 pt-6 px-6 pointer-events-none z-20">
            {/* Timeline dots — 8 positions */}
            <div className="flex justify-center items-center gap-1.5 mb-4">
                {WINDOWS.map((_, i) => (
                    <div
                        key={i}
                        className={`h-1 rounded-full transition-all duration-500 ${
                            i === wIdx ? "w-8 bg-meye-fuchsia" : "w-1 bg-zinc-700"
                        }`}
                    />
                ))}
            </div>

            {/* Window label + trainer greeting — colors flow on the global letter-cycle so they always sit ~180° opposite the drifting backdrop */}
            <div className="flex items-center justify-between text-[13px] sm:text-sm font-mono uppercase tracking-widest">
                <span className="letter-cycle font-brand font-bold" data-testid="screensaver-datetime">
                    {dateStr} · {timeStr}
                </span>
                <div className="flex items-center gap-2">
                    <ApertureEye size={14} spin={false} />
                    <span className="letter-cycle font-brand font-bold">
                        {userName ? userName.toLowerCase() : "trainer"}
                    </span>
                </div>
            </div>

            {/* Big window title */}
            <div className="text-center mt-3 fade-up" data-testid="screensaver-window-title">
                <p className="letter-cycle text-xs uppercase tracking-[0.4em] font-mono mb-1">
                    {w.subtitle}
                </p>
                <h2 className="letter-cycle font-brand text-3xl sm:text-4xl font-bold tracking-tight">
                    {(() => {
                        // "Later" (30-day view) shows the current month name — feels concrete, e.g. "August".
                        if (w.dynamicLabel === "monthAhead") {
                            return now.toLocaleString(undefined, { month: "long" });
                        }
                        return w.label;
                    })()}
                </h2>

                {/* COMMS shortcut removed per user request — the bottom search dock is the primary entry point now */}

                {/* Task / event counts for the current window — hidden on "later" because the
                    30-day calendar below already shows per-day counts (redundant + costs
                    vertical space that pushes the task pill into the COMMS button). */}
                {w.id !== "later" && (taskCount + eventCount) > 0 && (
                    <div
                        className="flex justify-center mt-2 pointer-events-none"
                        data-testid="screensaver-counts"
                    >
                        <div className="letter-cycle inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest">
                            <span data-testid="screensaver-task-count">
                                <span className="letter-cycle font-brand font-bold">{taskCount}</span>{" "}
                                {taskCount === 1 ? "task" : "tasks"}
                            </span>
                            {eventCount > 0 && (
                                <>
                                    <span className="opacity-50">·</span>
                                    <span data-testid="screensaver-event-count">
                                        <span className="letter-cycle font-brand font-bold">{eventCount}</span>{" "}
                                        {eventCount === 1 ? "event" : "events"}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Quick Inbox lives in the bottom action dock next to the voice-chat button. */}

                {/* Location-aware chip — appears when geofence surfaces tasks tied to a glossary
                    term (e.g. arriving at "Costco" surfaces the shopping list). Tap to jump the
                    card cycle to the first surfaced task. */}
                {contextScan?.surfaced_task_ids?.length > 0 && (
                    <div
                        className="flex justify-center mt-3 relative z-30 pointer-events-auto"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        data-testid="screensaver-location-chip-wrap"
                    >
                        <button
                            type="button"
                            onClick={() => onLocationTap?.(contextScan.surfaced_task_ids[0])}
                            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-meye-cyan/60 bg-meye-cyan/10 text-meye-cyan font-mono text-[11px] uppercase tracking-widest backdrop-blur-sm active:scale-95 transition-transform"
                            data-testid="screensaver-location-chip"
                        >
                            <span aria-hidden="true">◉</span>
                            <span>
                                @ {contextScan.city?.toLowerCase() || "here"} · {contextScan.surfaced_task_ids.length} tied to{" "}
                                {(contextScan.matched_terms?.[0]?.term || "").toLowerCase()}
                            </span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------- Right-side vertical task rail ----------
const TaskRail = ({ tIdx, total, tone }) => {
    const t = toneClass[tone];
    return (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 pointer-events-none">
            <ChevronUp size={12} className="text-zinc-700" />
            {Array.from({ length: Math.min(total, 8) }).map((_, i) => (
                <div
                    key={i}
                    className={`w-1 rounded-full transition-all duration-300 ${
                        i === Math.min(tIdx, 7) ? `h-6 ${t.bg} border ${t.border}` : "h-1 bg-zinc-700"
                    }`}
                />
            ))}
            {total > 8 && <span className="text-[9px] text-zinc-600 font-mono">+{total - 8}</span>}
            <ChevronDown size={12} className="text-zinc-700" />
        </div>
    );
};

// ---------- Day strip (weekly / monthly mini-calendar above the task pill) ----------
// Shows N slots starting from TODAY. Each slot displays dots for the number of events
// on that day. The slot for the currently-focused task's day is highlighted.
const WEEKDAY_ABBR = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const DayStrip = ({ windowTasks = [], currentTask, days = 7, onSelectOffset }) => {
    // Prefer the per-occurrence offset attached in currentTasks (week/later windows) so the
    // day-strip highlight follows the exact day the card belongs to — not the task's next hit.
    const focusOffset = cardOffset(currentTask);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Format a Date → "YYYY-MM-DD" using LOCAL date components (not UTC — critical for the
    // per-cell iso to match what taskOccursOnIso expects, otherwise late-evening drift into UTC
    // tomorrow would silently swap Friday's count with Saturday's).
    const fmtIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    // Per-day occurrence check. This matches the WeeklyView / MonthlyView source of truth so the
    // screensaver, weekly grid, and monthly grid always agree. Excluding snoozed + completed
    // non-recurring items to stay consistent with those views.
    // Also: `windowTasks` is the concatenation of today+tomorrow+week (or +later) buckets, so a
    // recurring task that hits multiple buckets is present multiple times. Dedupe by id here or
    // we would DOUBLE-COUNT it on every day it occurs.
    // Counts OPEN OBLIGATIONS per day (a moved beat + a native beat on one day = 2), judged by source key.
    const nowDate = new Date();
    const countOccursOn = (iso) => countOpenObligationsOn(windowTasks, iso, nowDate);
    // Only fire the parent callback if the day actually has a task — otherwise a click on a blank
    // day would silently reset the card cycle. The parent handles the "no tasks" case with a toast.
    const handleCellClick = (offset, count) => {
        if (!onSelectOffset) return;
        onSelectOffset(offset, count);
    };

    // ---- 30-day / month view: weekday-aligned grid starting on Sunday of the current week ----
    // Row 1 = Sun-Sat of THIS week. Days BEFORE today are rendered as blank cells so today lands
    // in its natural weekday column (e.g. if today is Saturday, TODAY sits at the right end of
    // row 1). Coverage: leading blanks + 30 forward days. Month divider + label appears whenever
    // we cross into a new calendar month.
    if (days > 14) {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday of the current week
        const totalCells = today.getDay() + 30; // leading blanks + 30 forward days
        const numRows = Math.ceil(totalCells / 7);
        const cells = [];
        for (let i = 0; i < numRows * 7; i++) {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            const offset = Math.round((d - today) / 86_400_000);
            const inRange = offset >= 0 && offset < 30;
            const count = inRange ? countOccursOn(fmtIso(d)) : 0;
            cells.push({ date: d, offset, count, inRange });
        }
        // Group by calendar month for the divider. Leading past days (this-week's Sun→yesterday
        // that fall before offset 0) are FOLDED INTO the first in-range month's group so they
        // share ONE 7-col grid — otherwise today lands in the Sunday column of the Aug grid
        // instead of its true weekday column. The trailing next-month cells stay in their own
        // group so we can drop the "SEP" divider row between them.
        const monthGroups = [];
        let current = null;
        for (const cell of cells) {
            const key = cell.inRange
                ? `${cell.date.getFullYear()}-${cell.date.getMonth()}`
                : cell.offset < 0
                    ? "leading"       // will be absorbed by the first in-range group below
                    : "trail";
            if (!current || current.key !== key) {
                const label = cell.inRange
                    ? cell.date.toLocaleDateString(undefined, { month: "short" }).toUpperCase()
                    : "";
                current = { key, label, cells: [] };
                monthGroups.push(current);
            }
            current.cells.push(cell);
        }
        // Absorb the leading past-days group into the first in-range group so they share a grid.
        if (monthGroups.length >= 2 && monthGroups[0].key === "leading") {
            const leading = monthGroups.shift();
            monthGroups[0].cells = [...leading.cells, ...monthGroups[0].cells];
        }
        // Pad every non-first in-range group with blank cells so its first day of the month
        // lands in its correct weekday column (e.g. if Sep 1 is Tuesday, prepend 2 blanks).
        for (let gi = 1; gi < monthGroups.length; gi++) {
            const grp = monthGroups[gi];
            if (grp.key === "trail" || !grp.cells.length) continue;
            const firstCell = grp.cells[0];
            if (!firstCell?.date) continue;
            const wkday = firstCell.date.getDay();
            if (wkday > 0) {
                const pads = Array.from({ length: wkday }).map((_, k) => ({
                    date: null, offset: -1000 - k, count: 0, inRange: false, isPad: true,
                }));
                grp.cells = [...pads, ...grp.cells];
            }
        }

        return (
            <div className="w-full max-w-xl mx-auto mt-0 mb-1 px-2 flex flex-col gap-0.5" data-testid="screensaver-day-strip" data-days={days}>
                {/* Weekday header — kept above the groups so it can never overlap a month label */}
                <div className="grid grid-cols-7 gap-1">
                    {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                        <div key={i} className="text-center text-[9px] font-mono text-zinc-500 uppercase tracking-widest">
                            {d}
                        </div>
                    ))}
                </div>
                {monthGroups.map((grp, gIdx) => (
                    <div key={grp.key + "-" + gIdx} className="flex flex-col gap-0.5">
                        {gIdx > 0 && grp.label && (
                            <div className="text-center text-[9px] font-mono text-zinc-500 uppercase tracking-widest pt-1">
                                {grp.label}
                            </div>
                        )}
                        <div className="grid grid-cols-7 gap-1">
                            {grp.cells.map(({ date, offset, count, inRange }, cIdx) => {
                                if (!inRange) {
                                    // Blank slot for leading past days / trailing future days
                                    return <div key={cIdx} className="h-8" data-testid={`day-strip-blank-${offset}`} />;
                                }
                                const isToday = offset === 0;
                                const isActive = focusOffset === offset;
                                const clickable = count > 0;
                                const day = date.getDate();
                                const labelText = isToday ? "TODAY" : String(day);
                                const sizeCls = isActive
                                    ? "scale-110 z-10 shadow-[0_0_22px_rgba(217,70,239,0.65)]"
                                    : "";
                                const border = isActive
                                    ? "border-2 border-meye-fuchsia bg-meye-fuchsia/20"
                                    : isToday
                                        ? "border border-meye-fuchsia/70 bg-meye-fuchsia/10"
                                        : count > 0
                                            ? "border border-meye-cyan/60 bg-meye-cyan/10"
                                            : "border border-zinc-500/50 bg-black/30";
                                const numCls = isActive
                                    ? isToday ? "text-meye-fuchsia text-[10px] tracking-widest" : "text-meye-fuchsia text-sm"
                                    : isToday
                                        ? "text-meye-fuchsia text-[9px] tracking-widest"
                                        : count > 0
                                            ? "text-meye-cyan text-xs"
                                            : "text-zinc-400 text-xs";
                                return (
                                    <div
                                        key={cIdx}
                                        className={`h-8 rounded-md transition-all duration-300 flex flex-col items-center justify-center ${sizeCls} ${border} ${clickable ? "cursor-pointer active:scale-110 pointer-events-auto" : "pointer-events-none"}`}
                                        data-testid={`day-strip-cell-${offset}`}
                                        data-active={isActive}
                                        data-today={isToday}
                                        data-clickable={clickable}
                                        onPointerDown={clickable ? (e) => { e.stopPropagation(); handleCellClick(offset, count); } : undefined}
                                    >
                                        <span className={`font-brand font-bold uppercase leading-none ${numCls}`}>{labelText}</span>
                                        {count > 0 && (
                                            <span className={`text-[9px] font-mono mt-0.5 leading-none font-bold ${isActive ? "text-meye-fuchsia" : "text-meye-cyan"}`}>
                                                +{count}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // ---- 7-day view (This Week) — original horizontal strip starting from TODAY ----
    const perDay = new Array(days).fill(0);
    for (let i = 0; i < days; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        perDay[i] = countOccursOn(fmtIso(d));
    }

    const renderCell = (globalIdx, count) => {
        const active = focusOffset === globalIdx;
        const clickable = count > 0;
        const cellDate = new Date(today);
        cellDate.setDate(today.getDate() + globalIdx);
        const label = globalIdx === 0 ? "TODAY" : WEEKDAY_ABBR[cellDate.getDay()];
        const sizeCls = active
            ? "py-3.5 scale-125 z-10 shadow-[0_0_28px_rgba(217,70,239,0.65)]"
            : "py-2.5";
        const labelSizeCls = active ? "text-sm" : "text-xs";
        return (
            <div
                key={globalIdx}
                className={`flex-1 min-w-0 rounded-lg border transition-all duration-300 flex flex-col items-center justify-center ${sizeCls} ${
                    active
                        ? "border-2 border-meye-fuchsia bg-meye-fuchsia/20"
                        : count > 0
                            ? "border border-meye-cyan/60 bg-meye-cyan/10"
                            : "border border-zinc-500/50 bg-black/30"
                } ${clickable ? "cursor-pointer active:scale-110 pointer-events-auto" : "pointer-events-none"}`}
                data-testid={`day-strip-cell-${globalIdx}`}
                data-active={active}
                data-clickable={clickable}
                onPointerDown={clickable ? (e) => { e.stopPropagation(); handleCellClick(globalIdx, count); } : undefined}
            >
                <span
                    className={`font-brand font-bold ${labelSizeCls} uppercase tracking-widest ${
                        active ? "text-meye-fuchsia" : count > 0 ? "text-meye-cyan" : "text-zinc-400"
                    }`}
                >
                    {label}
                </span>
                {count > 0 && (
                    <span className={`text-[10px] font-mono mt-0.5 font-bold ${active ? "text-meye-fuchsia" : "text-meye-cyan"}`}>
                        +{count}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div
            className="w-full max-w-xl mx-auto flex gap-1.5 items-end justify-center mb-4 px-2"
            data-testid="screensaver-day-strip"
            data-days={days}
        >
            {perDay.map((count, i) => renderCell(i, count))}
        </div>
    );
};

// ---------- Task focus card ----------
const TaskFocusCard = ({ task, window, badgeStyle = "solid", windowTasks = [], onSelectOffset, bodyRef }) => {
    const tone = toneClass[window.tone];
    const d = task.date ? daysDiff(task.date) : null;
    // Live countdown ticks every 30s so a task at 7:15 shows "in 3 min" then "in 2 min" without
    // the user having to reload. Only mounts a timer when we actually have a time-based today task.
    const [nowMs, setNowMs] = React.useState(Date.now());
    const showCountdown = window.id === "today" && !!task.time && !task.completed;
    React.useEffect(() => {
        if (!showCountdown) return undefined;
        const id = setInterval(() => setNowMs(Date.now()), 30_000);
        return () => clearInterval(id);
    }, [showCountdown]);
    // ADHD-friendly countdown copy — matches how a friend would say it out loud.
    const countdown = React.useMemo(() => {
        if (!showCountdown) return null;
        try {
            const [hh, mm] = task.time.split(":").map(Number);
            const target = new Date(); target.setHours(hh, mm, 0, 0);
            let diffMin = Math.round((target.getTime() - nowMs) / 60_000);
            if (diffMin >= 24 * 60 || diffMin <= -24 * 60) return null;
            if (diffMin === 0) return { text: "right now", tone: "urgent" };
            if (diffMin > 0) {
                if (diffMin <= 3) return { text: `in ${diffMin} min · any moment`, tone: "urgent" };
                if (diffMin < 60) return { text: `in ${diffMin} min`, tone: "soon" };
                // Round to nearest half-hour for readability.
                const hrs = Math.round((diffMin / 60) * 2) / 2;
                const label = Number.isInteger(hrs) ? `${hrs} hr` : `${hrs} hr`;
                return { text: `in ${label}`, tone: "later" };
            }
            // Negative → overdue
            const overdueMin = -diffMin;
            if (overdueMin < 60) return { text: `${overdueMin} min overdue`, tone: "overdue" };
            const hrs = Math.round((overdueMin / 60) * 2) / 2;
            return { text: `${hrs} hr overdue`, tone: "overdue" };
        } catch { return null; }
    }, [nowMs, showCountdown, task.time]);
    const countdownClass = countdown && {
        // App-native palette (fuchsia + cyan) instead of the semantic red-alert vibe.
        // Urgency conveyed by fill vs outline + pulse, not by red.
        urgent:  "text-white bg-meye-fuchsia border-meye-fuchsia shadow-[0_0_18px_rgba(217,70,239,0.5)] animate-pulse",
        soon:    "text-meye-fuchsia border-meye-fuchsia/60 bg-meye-fuchsia/10",
        later:   "text-meye-cyan border-meye-cyan/50 bg-meye-cyan/10",
        overdue: "text-white bg-meye-fuchsia/90 border-meye-fuchsia shadow-[0_0_18px_rgba(217,70,239,0.55)]",
    }[countdown.tone];

    let stamp = "TODO";
    // Recurring tasks are ongoing — never render "DONE" or strikethrough even when task.completed=true.
    // Handle them first so the completed-check below doesn't short-circuit.
    if (task.type === "recurring") {
        // Recurring tasks: derive the stamp from the WINDOW they're rendered in
        // (their anchor date is just a math reference, not their real due-ness).
        const winStamps = {
            today: "TODAY", tomorrow: "TOMORROW", week: "THIS WEEK",
            later: "LATER", someday: "SOMEDAY",
            overdue: "OVERDUE", older: "OVERDUE", completed: "DONE",
        };
        stamp = winStamps[window.id] || "RECURRING";
    } else if (task.completed) {
        stamp = "DONE";
    } else if (d === null) {
        stamp = "OPEN";
    } else if (d < 0) {
        stamp = `${Math.abs(d)} DAY${Math.abs(d) === 1 ? "" : "S"} AGO`;
    } else if (d === 0) {
        stamp = "TODAY";
    } else if (d === 1) {
        stamp = "TOMORROW";
    } else if (d <= 7) {
        stamp = `IN ${d} DAYS`;
    } else if (d <= 30) {
        stamp = `IN ${Math.floor(d / 7) || 1} WEEK${Math.floor(d / 7) > 1 ? "S" : ""}`;
    } else {
        stamp = `IN ${Math.floor(d / 30)} MONTH${Math.floor(d / 30) > 1 ? "S" : ""}`;
    }

    // GRADIENT mode: transparent pill + gradient text (opposite-hue gradient on letters)
    const isGradient = badgeStyle === "gradient";

    return (
        <div ref={bodyRef} className="w-full max-w-lg mx-auto flex flex-col items-center" data-testid={`screensaver-task-${task.id}`}>
            {/* Mini day strip — 7 days for This Week, 30 days for Later. Highlights the day of the
                task currently on-screen, so as the cards cycle you can see WHERE in the window we are. */}
            {(window.id === "week" || window.id === "later") && (
                <DayStrip
                    windowTasks={windowTasks}
                    currentTask={task}
                    days={window.id === "later" ? 30 : 7}
                    onSelectOffset={onSelectOffset}
                />
            )}

            <div
                className={`inline-flex items-stretch rounded-full border-2 ${tone.border} backdrop-blur-sm ${window.id === "later" ? "mt-1 mb-2" : "mb-10"} ${isGradient ? "bg-black/40" : ""}`}
                style={{
                    boxShadow: `0 0 32px ${tone.glow}`,
                    ...(isGradient ? {} : { backgroundColor: tone.solid }),
                }}
                data-testid="screensaver-badge"
                data-badge-style={badgeStyle}
            >
                <div
                    className={`${window.id === "later" ? "px-4 py-1.5" : "px-5 py-2.5"} font-brand font-black text-xs sm:text-sm uppercase tracking-widest ${
                        isGradient
                            ? "letter-cycle-flow"
                            : "text-black"
                    }`}
                >
                    {stamp}
                </div>
                {task.time && (
                    <>
                        <div className={`w-px my-2 ${isGradient ? "bg-white/20" : "bg-black/30"}`} aria-hidden="true" />
                        <div
                            className={`${window.id === "later" ? "px-3 py-1.5" : "px-4 py-2.5"} font-brand font-black text-xs sm:text-sm uppercase tracking-widest flex items-center gap-1.5 ${
                                isGradient
                                    ? "letter-cycle-flow"
                                    : "text-black"
                            }`}
                        >
                            <Clock size={12} strokeWidth={2.5} aria-hidden="true" className={isGradient ? "letter-cycle" : "text-black"} />
                            {formatTime(task.time)}
                        </div>
                    </>
                )}
            </div>

            {/* Live countdown chip removed from here — it now renders BELOW the task title,
                see the block immediately after the h2. */}

            <h2
                className={`font-brand font-bold leading-[1.1] tracking-tight ${
                    window.id === "later"
                        ? "text-xl sm:text-2xl"
                        : "text-4xl sm:text-5xl md:text-6xl"
                } ${
                    task.completed && task.type !== "recurring"
                        ? "line-through text-zinc-500"
                        : isGradient
                            ? "letter-cycle-flow"
                            : "text-zinc-100"
                }`}
                data-testid="screensaver-task-text"
            >
                <SafeLinkText text={task.text} />
            </h2>
            {taskBodyPreview(task) && (
                <SafeLinkText as="p" text={taskBodyPreview(task)} className="mt-2 text-sm sm:text-base text-zinc-400 leading-snug line-clamp-2 max-w-md mx-auto" data-testid="screensaver-task-body" />
            )}
            {task._movedFrom && (
                <div className="mt-1 inline-flex items-center text-[10px] font-brand font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-amber-400/50 bg-amber-500/10 text-amber-200" data-testid="screensaver-moved-from">
                    moved from {formatDate(task._movedFrom)}
                </div>
            )}

            {/* Live countdown chip — ADHD-friendly at-a-glance "how long till this?". Renders only
                for TODAY tasks that have a time set. Ticks every 30s. Uses the app's fuchsia +
                cyan palette instead of red/amber so it feels native. Rendered BELOW the task
                title per user preference so the eye lands on the task first, then the timing. */}
            {countdown && (
                <div
                    className={`mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border font-mono text-[11px] font-bold uppercase tracking-widest ${countdownClass}`}
                    data-testid="task-countdown"
                >
                    <Clock size={11} strokeWidth={2.5} aria-hidden="true" />
                    {countdown.text}
                </div>
            )}

            {task._missedCount > 0 && (
                <div
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-red-500/50 bg-red-500/15 text-red-200 font-brand text-[11px] font-bold uppercase tracking-widest"
                    data-testid="screensaver-missed-count"
                    title={`${task._missedCount} outstanding · oldest ${task._oldestMissedIso}`}
                >
                    {task._missedCount} missed · oldest {task._oldestMissedIso}
                </div>
            )}

            {/* Two-clock visualiser — pairs the wall-clock countdown chip above with an actual
                dial-based view of NOW vs TASK time. Only shows when the user is viewing the
                TODAY window on the screensaver. A daily recurring task ALSO appears in the
                Tomorrow / Week windows now, but the clock would be misleading there — it'd
                render as if the beat is today. Gate on the visible window, not on the task's
                anchor date. */}
            {window?.id === "today" && task.time && (
                <div className="mt-3">
                    <TaskTimeClocks taskTime={task.time} taskDate={task.date} />
                </div>
            )}
        </div>
    );
};

// ---------- Empty window card ----------
const EmptyCard = ({ window }) => {
    const tone = toneClass[window.tone];
    // For "clean" windows (no events) we replace the boring string with a lush
    // Carrot-style forecast card. Compute the target date the window represents.
    const targetDate = useMemo(() => {
        const d = new Date();
        if (window.id === "today") return d.toISOString().slice(0, 10);
        if (window.id === "tomorrow") { d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); }
        if (window.id === "week")     { d.setDate(d.getDate() + 3); return d.toISOString().slice(0, 10); }
        if (window.id === "later")    { d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); }
        return null;
    }, [window.id]);

    // Only show weather for future-facing windows — historical ones just keep the label.
    const showWeather = ["today", "tomorrow", "week", "later"].includes(window.id);

    if (!showWeather) {
        return (
            <div className="text-center fade-up">
                <ApertureEye size={56} className="mx-auto mb-6 opacity-50" />
                <p
                    className={`font-brand font-bold text-3xl sm:text-4xl ${tone.text} tracking-tight`}
                    data-testid="screensaver-empty"
                >
                    {window.empty}
                </p>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col items-center fade-up px-4" data-testid="screensaver-empty">
            <WeatherCard targetDate={targetDate} tone={window.tone} label={window.empty} />
        </div>
    );
};

// ---------- Screensaver bottom search dock ----------
// Renders a search input + mic button. Typing/submitting forwards the query to the app's
// global search (via onSearch), which then dismisses the screensaver and shows results
// in the same grouped inline view the app already uses.
const ScreensaverSearchDock = ({ onSearch, onOpenComms, onQuickCaptured, assistantName, assistantAvatarId }) => {
    const [q, setQ] = useState("");
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);

    const submit = (value) => {
        const clean = (value ?? q).trim();
        if (!clean) return;
        onSearch(clean);
    };

    const startRec = async (e) => {
        e.stopPropagation();
        if (recording || transcribing) {
            // second tap = stop
            recorderRef.current?.stop();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const rec = new MediaRecorder(stream);
            recorderRef.current = rec;
            chunksRef.current = [];
            rec.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
            rec.onstop = async () => {
                setRecording(false);
                streamRef.current?.getTracks()?.forEach((t) => t.stop());
                if (chunksRef.current.length === 0) {
                    toast.warning("Nothing recorded");
                    return;
                }
                setTranscribing(true);
                try {
                    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
                    const { text } = await api.transcribe(blob);
                    const clean = (text || "").trim();
                    if (!clean) {
                        toast.warning("Didn't catch that");
                    } else {
                        setQ(clean);
                        submit(clean);
                    }
                } catch (err) {
                    toast.error("Transcription failed");
                    console.error(err);
                } finally {
                    setTranscribing(false);
                }
            };
            // Timeslice keeps ondataavailable firing every 500ms — required on mobile Safari
            // so at least one chunk is captured before stop() completes.
            rec.start(500);
            setRecording(true);
        } catch (err) {
            toast.error("Microphone unavailable");
            console.error(err);
        }
    };

    return (
        <div
            className="absolute bottom-4 left-0 right-0 flex flex-col items-center gap-2 z-40 px-4"
            data-testid="screensaver-search-dock"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            {/* Floating action row — single Let's Chat button, styled as a subtle eye to echo the mEYEtasks brand. */}
            <div className="flex items-center gap-3 -mb-1">
                {onOpenComms && (
                    <button
                        type="button"
                        onPointerDown={(e) => {
                            // Immediate haptic + brief compression BEFORE the state change so the
                            // press feels physical. `transform` re-runs even if the button unmounts.
                            try { navigator.vibrate?.(20); } catch (_) { /* no-op */ }
                            const btn = e.currentTarget;
                            btn.style.transform = "scale(0.96)";
                            btn.style.transition = "transform 90ms ease-out";
                            setTimeout(() => {
                                btn.style.transform = "";
                            }, 100);
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            unlockAudio();
                            onOpenComms();
                        }}
                        className="p-2 flex items-center justify-center meye-comm-halo rounded-full bg-transparent border-0 relative"
                        data-testid="screensaver-comms-btn"
                        aria-label="Open voice chat"
                        style={{ touchAction: "manipulation" }}
                    >
                        <CircularAudioIcon size={56} />
                    </button>
                )}
            </div>

            <div className="flex items-center gap-2 w-full max-w-md rounded-full border border-meye-border/70 bg-black/60 backdrop-blur-md pl-4 pr-1.5 py-1.5 shadow-[0_0_18px_rgba(34,211,238,0.18)]">
                <Search size={14} strokeWidth={1.75} className="text-zinc-500 flex-shrink-0" />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    placeholder="search everything..."
                    maxLength={80}
                    className="flex-1 bg-transparent border-none outline-none text-sm text-zinc-100 placeholder:text-zinc-600 font-mono min-w-0"
                    data-testid="screensaver-search-input"
                    autoComplete="off"
                    inputMode="search"
                />
                {q && (
                    <button
                        type="button"
                        onClick={() => setQ("")}
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800/60 transition-colors"
                        data-testid="screensaver-search-clear"
                        aria-label="Clear search"
                    >
                        <X size={14} strokeWidth={2.5} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={startRec}
                    disabled={transcribing}
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                        recording
                            ? "bg-meye-cyan text-black animate-pulse"
                            : transcribing
                                ? "bg-meye-cyan/20 text-meye-cyan"
                                : "bg-meye-cyan/15 text-meye-cyan hover:bg-meye-cyan/30"
                    }`}
                    data-testid="screensaver-search-mic"
                    aria-label={recording ? "Stop recording" : "Speak to search"}
                    title="Speak to search"
                >
                    {transcribing ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} strokeWidth={2} />}
                </button>
            </div>
        </div>
    );
};

export default Screensaver;
